'use client';

// Requirements Summary:
// Public program details use coach wording and only show coach gender when the
// program's family-facing coach gender setting allows it.
//
// OPEN GYM (U5, R6/R7): a class flagged is_open_gym shows its own identity here —
// an "Open Gym" hero badge and a single "Book a session" CTA that routes to the
// canonical drop-in path (handleBookDropIn → ?type=drop_in). The dual
// enrollment/drop-in pricing selector is skipped (open gym is pay-per-visit only);
// the simple drop-in price + U6 member-rate line render instead. When the detail
// API returns members_only_locked (a members-only open gym the viewer can't
// book), a plain-English "members only" notice REPLACES the booking CTA while the
// schedule/price stay visible to motivate joining. The server is the real gate.

import { classSellsMonthly } from '@/utils/programs/classSellsMonthly';
import { classHasPaidPrice as classHasAnyPaidPrice } from '@/utils/billing/storedPrice';
import { memberDropInPriceToShow } from '@/utils/pricing/memberRateHigherThanStandard';
import { useState, useEffect, type ReactNode } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import Link from 'next/link';
import GoogleMaterialIcon from '@/components/ui/GoogleMaterialIcon';
import PublicPriceExtrasNote from '@/components/programs/PublicPriceExtrasNote';
import { createLogger } from '@/utils/logger/devLogger';
import { useUserRole } from '@/context/UserRoleContext';
import { PublicAccountMenu } from '@/components/common/PublicAccountMenu';
import { useSelfServiceAccess } from '@/hooks/auth/useSelfServiceAccess';
import { useIsMobileWebView } from '@/hooks/useIsMobileWebView';
import { useOrganization } from '@/context/OrganizationContext';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import Alert from '@/components/ui/alert/Alert';
import Typography from '@/components/ui/Typography';
import PageLoading from '@/components/common/PageLoading';
import SummaryBelowPlaceholder from '@/components/seo/SummaryBelowPlaceholder';
import { useKeepScrollAcrossReload } from '@/hooks/ui/useKeepScrollAcrossReload';
import { StaffRegistrationMessage } from '@/components/common/StaffRegistrationGuard';
import PreviewAsParentButton from '@/components/programs/registration/preview/PreviewAsParentButton';
import { formatDate, formatDateOnly, formatDateWithDayNoYear, parseDateLocal } from '@/utils/formatters/dateUtils';
import { GenderUtils } from '@/utils/constants/genderConstants';
import CoachAvatar from '@/components/ui/avatar/CoachAvatar';
import { WEEKDAY_NAMES, WEEKDAY_NAMES_SHORT } from '@/utils/constants/weekdays';
import { summarizeWeeklyDays } from '@/utils/classes/weeklyDaySummary';
import { brandTextColor, readableTextOn, SURFACE_GRAY_50 } from '@/utils/branding/brandColorContrast';
import { NEXT_SESSION_LOOKAHEAD_DAYS, getGymNowParts } from '@/utils/programs/nextOpenGymSession';
import { buildNextSessionLine, type NextSessionAvailabilityData } from '@/utils/programs/nextSessionLine';
import { getPublicEnrollmentGating } from '@/utils/programs/openGymEnrollmentGating';
import { isAddedDateSlot, toDateOnly } from '@/utils/classes/oneOffScheduleSlots';
import { SESSION_PAYMENT_PLAN_OPTIONS, isSessionPaymentPlan, type SessionPaymentPlan } from '@/utils/billing/sessionPaymentPlan';
import { isInvitedAthleteLogin } from '@/utils/auth/parentDashboardAccess';
import { isProgramEnded, publicProgramGymToday } from '@/utils/programs/isProgramEnded';
import { useFamilyAthleteGate } from '@/hooks/family/useFamilyAthleteGate';
import { CANNOT_REGISTER_ATHLETE_MESSAGE } from '@/utils/family/registrationPermissionCopy';

const logger = createLogger('PublicProgramDetail');

// Helper function to convert day_of_week number to day name
const getDayName = (dayNum: number): string => {
  return WEEKDAY_NAMES[dayNum] || '';
};

/**
 * The exact dates a session-based class meets (#2119, public half).
 *
 * A parent signing up for "the four Sundays in August" should see those four
 * dates, not just "Sundays" and a date range. Dates the class does not meet —
 * whether the gym blocked that one class or closed the whole gym — are crossed
 * out in place rather than dropped, so a missing date never looks like a bug.
 *
 * `meeting_dates` / `skipped_dates` are computed server-side by the public
 * program route; recurring classes never receive them and render nothing here.
 */
const SessionDatesList = ({
  meetingDates,
  skippedDates,
  truncated,
}: {
  meetingDates?: string[];
  skippedDates?: string[];
  truncated?: boolean;
}) => {
  const dates = [
    ...(meetingDates || []).map((date) => ({ date, meets: true })),
    ...(skippedDates || []).map((date) => ({ date, meets: false })),
  ].sort((a, b) => a.date.localeCompare(b.date));

  if (dates.length === 0) return null;

  const meetingCount = dates.filter((d) => d.meets).length;
  const hasClosures = dates.length > meetingCount;

  return (
    <div className="mt-3">
      <div className="mb-1.5 text-xs font-medium uppercase tracking-wider text-gray-500">
        {meetingCount === 1 ? '1 class date' : `${meetingCount} class dates`}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {dates.map(({ date, meets }) => (
          <span
            key={date}
            title={meets ? undefined : 'No class on this date'}
            className={
              meets
                ? 'inline-flex items-center rounded-md bg-white px-2 py-0.5 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-200'
                : 'inline-flex items-center rounded-md bg-orange-50 px-2 py-0.5 text-xs font-medium text-orange-700 line-through ring-1 ring-inset ring-orange-200'
            }
          >
            {formatDateWithDayNoYear(date)}
            {/* The strikethrough is visual only — say it in words for screen readers. */}
            {!meets && <span className="sr-only"> — no class on this date</span>}
          </span>
        ))}
      </div>
      {hasClosures && (
        <p className="mt-1.5 text-xs text-gray-500">
          Crossed-out dates are closed &mdash; there is no class that day.
        </p>
      )}
      {/* Only reachable on a bad session end date (e.g. a year typo), which the
          class form does not validate. Without this the list just stops at the
          cap and silently looks like the whole run. Parent-facing wording: the
          gym has to fix it, so point at them rather than blame the data. */}
      {truncated && (
        <p className="mt-1.5 text-xs text-gray-500">
          Showing the first {dates.length} dates. Contact the gym for the full schedule.
        </p>
      )}
    </div>
  );
};

// Helper function to format time from HH:MM to readable format
const formatTime = (time: string): string => {
  try {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  } catch {
    return time;
  }
};


interface ProgramDetail {
  id: string;
  name: string;
  program_type: 'class' | 'team';
  // Open Gym U5 (R7): discriminator the detail page keys on for the distinct
  // identity (badge, cover icon, "Book a session" CTA). Server-supplied.
  is_open_gym?: boolean;
  // Open Gym U4 (R6): true when this is a members-only Open Gym and the viewer is
  // NOT an active member at the offering's location (anonymous → true). When set
  // we show a plain-English "members only" notice instead of the booking form.
  members_only_locked?: boolean;
  open_gym_members_only?: boolean;
  // U3: set by the detail route when this offering's payment cannot be routed.
  // `registration_locked` means EVERY location it runs at is blocked — the
  // booking form is replaced by the notice. `registration_locked_notice` means
  // any one of them is, which is what the notice itself is owed on: a class at a
  // blocked AND a working location keeps its booking form and still says so.
  registration_locked?: boolean;
  registration_locked_notice?: boolean;
  registration_locked_locations?: Array<{ id: string; name: string }>;
  // Open Gym U6 (R12): member drop-in rate fields for the "$X / $Y members"
  // display (already consumed on this page via the (program as any) accessors).
  member_drop_in_enabled?: boolean;
  member_drop_in_rate?: number | null;
  // Resolved IANA timezone for the "Next session" line's gym-local now/date math.
  timezone?: string | null;
  // The gym's own clock, used for "has this class ended" (same as the enroll API).
  gym_timezone?: string | null;
  // Explicit closed dates for the "Next session" line's closed-date set, and for
  // the schedule-window display below the calendar.
  unavailable_dates?: string[];
  // Session-based bounds (null for ongoing classes): pre-season shows the
  // season's first session; past the end date the line disappears.
  session_start_date?: string | null;
  session_end_date?: string | null;
  extra_drop_ins_allowed?: boolean;
  description: string | null;
  age_range_min: number | null;
  age_range_max: number | null;
  skill_level: string | null;
  schedule_description: string | null;
  team_type?: string | null; // For team programs
  season_name?: string | null; // For team programs
  season_start_date?: string | null; // For team programs
  season_end_date?: string | null; // For team programs
  location?: {  // Single location (camps/events, teams)
    id: string;
    name: string;
    city: string;
    state: string;
  } | null;
  schedule_locations?: Array<{  // Multiple locations (classes)
    id: string;
    name: string;
    city: string;
    state: string;
  }>;
  monthly_rate: number | null;
  drop_in_rate: number | null;
  drop_in_enabled?: boolean;
  monthly_membership_enabled?: boolean;
  drop_in_availability_rule?: 'always' | 'only_if_not_full' | 'extra_slots';
  trial_rate: number | null;
  capacity: number | null;
  current_enrollment: number;
  // null for open gyms: the public API neutralizes the lifetime number there
  // because capacity is per session.
  spots_remaining: number | null;
  instructor_name: string | null;
  instructor_bio: string | null;
  instructor_credentials: string | null;
  start_date: string | null;
  end_date: string | null;
  /** #4724: 'YYYY-MM-DD' while an ongoing class's first class is still ahead (gym-local); else null. */
  starts_on?: string | null;
  enrollment_status: 'open' | 'closed' | 'waitlist_only';
  waitlist_enabled?: boolean;
  visibility: 'visible' | 'hidden' | 'private_link';
  features: string[] | null;
  cover_image_url: string | null;
  registration_type?: 'direct_enrollment' | 'evaluation_required';
  billing_type: 'monthly' | 'one_time' | 'hybrid' | 'fixed_term';
  is_session_based?: boolean;
  session_total_price?: number | null;
  session_payment_plan?: SessionPaymentPlan | null;
  public_settings: {
    trial_enabled: boolean;
    trial_description: string | null;
    custom_message: string | null;
  } | null;
  sections?: Array<{
    id: string;
    section_name: string;
    capacity: number;
    current_enrollment?: number;
    spots_remaining?: number | null;
    waitlist_enabled?: boolean;
    waitlist_waiting_count?: number;
    location_id?: string;
    // U3: this schedule's own location has no usable payment account. Present
    // only when the gym has a lock; absent means bookable.
    registration_locked?: boolean;
    instructor?: {
      id?: string | null;
      display_name: string | null;
      preferred_name?: string | null;
      first_name: string | null;
      last_name: string | null;
      gender: string | null;
      avatar_url?: string | null;
    } | null;
    schedule: Array<{
      id?: string;
      day_of_week: number;
      start_time: string;
      end_time: string;
      location_id?: string;
      location_name?: string | null;
      instructor_name?: string | null;
      instructor_names?: string[];
      instructors?: Array<{
        id?: string | null;
        display_name: string | null;
        preferred_name?: string | null;
        first_name: string | null;
        last_name: string | null;
        gender: string | null;
        avatar_url?: string | null;
      }>;
      instructor?: {
        id?: string | null;
        display_name: string | null;
        preferred_name?: string | null;
        first_name: string | null;
        last_name: string | null;
        gender: string | null;
        avatar_url?: string | null;
      } | null;
    }>;
  }>;
  class_schedule?: Array<{
    day_of_week: number;
    start_time: string;
    end_time: string;
    location_id?: string;
  }>;
}

interface OrganizationInfo {
  id?: string;
  name: string;
  /** Free plan roster limit reached: no new athletes online (per request). */
  roster_cap_blocked?: boolean;
  logo_url: string | null;
  slug: string;
  branding?: {
    primary_color: string | null;
    secondary_color: string | null;
  };
}

export interface ProgramDetailClientProps {
  /**
   * The route params, ALREADY RESOLVED.
   *
   * This used to be the `Promise` the App Router hands a page, read with
   * `use()`. The server page now awaits them itself — it has to, to load the
   * SEO payload — so passing the promise on would make the client suspend a
   * second time on a value its parent already holds.
   */
  params: { gymSlug: string; programType: string; programId: string };
  /**
   * The crawler-visible program summary the SERVER already rendered, or null
   * when this program is not publishable.
   *
   * It is displayed below the loading screen, so the served HTML carries the
   * program's real content without a person seeing it flash up, and the client
   * swaps both for the interactive page once its own fetch resolves. Passed as
   * a node rather than as data on purpose: the summary is a server component,
   * and rendering it here would make this whole route a client render again.
   *
   * THE CLIENT KEEPS ITS OWN MOUNT FETCHES and is not seeded from this. It
   * consumes member-only rates and live open-gym availability, which the SEO
   * fetcher is forbidden to return (KTD5) and an hour-cached page could not own
   * anyway.
   */
  serverSummary?: ReactNode;
}

export default function ProgramDetailPage(props: ProgramDetailClientProps) {
  const params = props.params;
  const serverSummary = props.serverSummary ?? null;
  const router = useRouter();
  const pathname = usePathname();
  const gymSlug = params.gymSlug;
  const programId = params.programId;
  const { currentUser, isLoading: loadingRole } = useUserRole();
  const selfServiceAccess = useSelfServiceAccess();
  // Inside the app's webview the parent already has the app's own navigation, so
  // the header's account controls are hidden here the same way they are on the
  // catalog this page is opened from.
  const isMobileWebView = useIsMobileWebView();
  const { currentOrganization } = useOrganization();
  // Plan 2026-05-23-002 PR #544 remediation (ADV-003): "non-self-managing
  // minor athlete" guard. Post-U3 backfill, adult athletes resolve as
  // userType='parent' (with a self-relationship row), so this correctly
  // does NOT fire for them — they fall through to the standard enrollment
  // path. Do not widen this check; doing so would block legitimate
  // self-enrollment for the U3 cohort.
  const isInvitedAthlete = isInvitedAthleteLogin(currentUser);

  const [organization, setOrganization] = useState<OrganizationInfo | null>(null);
  const familyGate = useFamilyAthleteGate(organization?.id ?? null);
  const [program, setProgram] = useState<ProgramDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDropInInfo, setShowDropInInfo] = useState(false);
  const [selectedPricingOption, setSelectedPricingOption] = useState<'enrollment' | 'drop_in'>('enrollment');
  // The ONE availability fetch's response over the lookahead window. Stays
  // null while pending and after a failed/403 fetch — the Next session line
  // then renders time-only from unavailable_dates.
  const [nextSessionAvailability, setNextSessionAvailability] =
    useState<NextSessionAvailabilityData | null>(null);
  // Bumped by the Try again button, which is the whole point of it: the fetch
  // below reads it, so incrementing re-runs the load without a full page reload.
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        logger.info('Fetching program details:', {
          gymSlug,
          programId,
          programType: params.programType
        });

        // Fetch organization
        const orgResponse = await fetch(`/api/public/organizations/${gymSlug}`);
        if (!orgResponse.ok) throw new Error('Organization not found');
        const orgData = await orgResponse.json();
        setOrganization(orgData.organization);

        // Fetch program details
        const programResponse = await fetch(
          `/api/public/programs/${params.programType}/${programId}?gymSlug=${gymSlug}`
        );
        if (!programResponse.ok) {
          const errorData = await programResponse.json().catch(() => ({}));
          logger.error('Program fetch failed:', { status: programResponse.status, error: errorData });
          throw new Error('Program not found');
        }
        const programData = await programResponse.json();
        setProgram(programData.program);
      } catch (err) {
        logger.error('Failed to fetch program details:', err);
        setError('Unable to load program details');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [gymSlug, programId, params.programType, retryCount]);

  // Fire ONE anonymous availability fetch over the lookahead window once the
  // program payload confirms this is an open gym. The response carries the
  // closed-dates union (unavailable_dates + gym_closings) and the per-date
  // counts the spots fragment needs. Any failure — including the 403 a
  // members-only open gym returns to non-members — silently degrades to the
  // time-only line.
  useEffect(() => {
    if (!(program?.program_type === 'class' && program?.is_open_gym === true)) return;
    let cancelled = false;

    const loadAvailability = async () => {
      try {
        const { dateStr } = getGymNowParts(program?.timezone);
        const base = parseDateLocal(dateStr);
        const end = new Date(
          base.getFullYear(),
          base.getMonth(),
          base.getDate() + NEXT_SESSION_LOOKAHEAD_DAYS,
          12
        );
        const response = await fetch(
          `/api/public/programs/availability?programId=${programId}&startDate=${dateStr}&endDate=${formatDateOnly(end)}`
        );
        if (!response.ok) return; // 403 / failure → time-only line, no error UI
        const data = await response.json();
        if (!cancelled) setNextSessionAvailability(data);
      } catch {
        // Network failure → time-only line, no error UI
      }
    };

    loadAvailability();
    return () => {
      cancelled = true;
    };
  }, [program, programId]);

  const getProgramTypeLabel = (type: string) => {
    const labels = {
      class: 'Class',
      team: 'Team Program'
    };
    return labels[type as keyof typeof labels] || type;
  };

  const handleEnrollNow = () => {
    // Go directly to enrollment page - it handles auth check internally
    const queryParams = new URLSearchParams();
    if (program?.billing_type === 'fixed_term') {
      queryParams.set('type', 'fixed_term');
    } else if (program?.billing_type === 'hybrid' || program?.billing_type === 'monthly') {
      queryParams.set('type', 'monthly');
    }

    let url = `/programs/${gymSlug}/${params.programType}/${programId}/enroll`;
    if (queryParams.toString()) url += `?${queryParams.toString()}`;
    router.push(url);
  };

  // Keeps a `section` the page was opened with, as the parent buttons' links do not.
  const previewEnrollHref = (() => {
    const query = new URLSearchParams({ preview: '1' });
    const section = typeof window === 'undefined' ? null : new URLSearchParams(window.location.search).get('section');
    if (section) query.set('section', section);
    return `/programs/${gymSlug}/${params.programType}/${programId}/enroll?${query.toString()}`;
  })();

  const handleBookDropIn = () => {
    const queryParams = new URLSearchParams();
    queryParams.set('type', 'drop_in');
    
    const url = `/programs/${gymSlug}/${params.programType}/${programId}/enroll?${queryParams.toString()}`;
    router.push(url);
  };

  const handleJoinWaitlist = () => {
    const queryParams = new URLSearchParams();
    // Pin monthly on a regular class so hybrid "Choose Your Path" cannot
    // switch to drop-in and charge. Open Gym has no monthly path; enroll
    // still waitlists because waitlist-only is billing-type-independent.
    const openGym = program?.program_type === 'class' && program?.is_open_gym === true;
    if (!openGym) {
      queryParams.set('type', 'monthly');
    }
    let url = `/programs/${gymSlug}/${params.programType}/${programId}/enroll`;
    if (queryParams.toString()) url += `?${queryParams.toString()}`;
    router.push(url);
  };

  // A reload part-way down would otherwise land in the crawler summary under the spinner.
  useKeepScrollAcrossReload(!loadingRole && !loading);

  if (loadingRole) {
    // Same reason as the branch below: the crawler-visible summary stays in the
    // page, under the spinner, so a crawler that stops rendering early keeps it.
    const spinner = <PageLoading variant="full-page" message="Checking your account..." />;
    if (serverSummary) return <SummaryBelowPlaceholder placeholder={spinner} summary={serverSummary} />;
    return spinner;
  }

  // Browsing is open to everyone; only the sign-up CTA is withheld. Covers
  // confirmed staff AND a viewer we could not identify — StaffRegistrationMessage
  // picks the right explanation.
  const selfServiceUnavailable = selfServiceAccess === 'staff' || selfServiceAccess === 'unconfirmed';

  if (loading) {
    // The server already wrote this program into the first response. It stays
    // in the page for crawlers, below the spinner, so a person never sees it
    // flash up before the real page replaces it.
    const spinner = <PageLoading variant="full-page" message="Loading program details..." />;
    if (serverSummary) return <SummaryBelowPlaceholder placeholder={spinner} summary={serverSummary} />;
    return spinner;
  }

  if (error || !program) {
    // THE SERVER ALREADY WROTE THIS PROGRAM, SO AN ERROR SCREEN WOULD BE
    // DELETING CONTENT THE PAGE ALREADY HAS. Two readers make this
    // load-bearing rather than tidy:
    //
    //  - GOOGLEBOT RENDERS. It fetches the page, gets the summary, then runs
    //    the mount fetch above — which asks `/api/public/...`, and robots.txt
    //    disallows `/api/`. Google's renderer refuses that request, this branch
    //    catches it, and without this guard the DOM Google indexes is "Error"
    //    with no program, price or schedule in it. The whole unit's value would
    //    reach every crawler EXCEPT the one that matters most, and no
    //    server-render test could see it.
    //  - A PERSON whose connection dropped is better served by the program's
    //    real details plus a way back than by a screen that throws them away.
    //
    // Only the "Error" heading goes, so the page keeps exactly one `<h1>` — the
    // summary's.
    if (serverSummary) {
      return (
        <div className="min-h-screen bg-white">
          {serverSummary}
          {/*
            `role="alert"`, because this message is APPENDED to a page that
            already looks finished. Before this change the whole screen was
            replaced by an "Error" heading, which a screen reader announced on
            its own; now a non-sighted visitor would otherwise be handed a
            complete-sounding program page and never told the sign-up half
            failed.
          */}
          <div
            role="alert"
            className="mx-auto max-w-5xl px-4 pb-10 sm:px-6 lg:px-8"
          >
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <GoogleMaterialIcon name="warning" size="xs" className="mt-0.5 text-amber-600" />
              <div>
                {/*
                  A FIXED SENTENCE, never the caught error's own text. `error`
                  here can be a browser string like "Failed to fetch", which is
                  the developer jargon the plain-English rule bans and means
                  nothing to a parent. One line for what happened, one for what
                  to do — the second is on the button.
                */}
                <p className="text-sm text-amber-900">
                  We couldn&apos;t load the sign-up options for this program.
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setError(null);
                      setLoading(true);
                      setRetryCount((count) => count + 1);
                    }}
                  >
                    Try again
                  </Button>
                  <Button variant="outline" onClick={() => router.push(`/programs/${gymSlug}`)}>
                    ← Back to Programs
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <GoogleMaterialIcon name="error" size="lg" className="text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Error</h1>
          <p className="text-gray-600">{error || 'Program not found'}</p>
          <Button variant="outline" className="mt-4" onClick={() => router.push(`/programs/${gymSlug}`)}>
            ← Back to Programs
          </Button>
        </div>
      </div>
    );
  }

  // Open Gym U5 (R7): the detail page reads the discriminator to swap in the
  // distinct identity (badge + "Book a session" CTA). Open gym is always
  // program_type='class' under the hood. Derived BEFORE the gating predicate
  // below, which needs it.
  const isOpenGym = program.program_type === 'class' && program.is_open_gym === true;
  /**
   * #4968: show a monthly price only when the class actually sells monthly and a
   * price is saved. `monthly_rate` is 0 (not null) for a free class, so a
   * truthiness check would print a stray "0" and hide the real $0 line.
   */
  const monthlyPriceShown = classSellsMonthly(program as any) && program.monthly_rate != null;
  // #5237: the "+ tax and fees" note, for a class that costs something.
  const classHasPaidPrice =
    program.program_type === 'class' &&
    classHasAnyPaidPrice(program as any, ((program as any).package_pricing ?? []).map((pkg: any) => pkg?.price));

  // Split the case — an open gym gates only on explicit staff intent
  // (waitlist_only/closed); the lifetime spots number (null from the API)
  // never flips it to waitlist. Regular classes keep the existing spots === 0
  // gate. Shared predicate with the catalog so both surfaces classify
  // identically.
  const { waitlistOnly: isWaitlistOnly, closed: gatingClosed } = getPublicEnrollmentGating({
    isOpenGym,
    enrollmentStatus: program.enrollment_status,
    spotsRemaining: program.spots_remaining,
    classOffer: program.program_type === 'class' ? program : null,
  });
  // A fixed-term class past its end date takes no new sign-ups; the catalog
  // card links here, so this page says so rather than offering Enroll Now.
  // Same check as the catalog, the enroll page and the enroll API.
  const programEnded = isProgramEnded(program, publicProgramGymToday(program));
  const isClosed = gatingClosed || programEnded;

  const primaryColor = organization?.branding?.primary_color || '#2563eb';
  const classSections = program.sections || [];
  const memberDropInPriceShown = memberDropInPriceToShow({
    isOpenGym,
    scheduleCount: program.program_type === 'class' ? classSections.length : null,
    memberDropInEnabled: Boolean(program.member_drop_in_enabled),
    memberDropInRate: program.member_drop_in_rate,
    standardDropInRate: program.drop_in_rate,
    dropInEnabled: program.drop_in_enabled,
  });
  // Open Gym U4 (R6): a members-only open gym that the current viewer can't book
  // (anonymous, or a signed-in non-member). When true we render a plain-English
  // "members only" notice in place of the booking CTA. The server is the real
  // gate (availability/enroll reject non-members); this is the friendly UI.
  const membersOnlyLocked = isOpenGym && program.members_only_locked === true;

  // U3 (R2, R8 / KTD3, KTD7, KTD8). The route answered two separate questions:
  // whether every location this offering runs at is blocked (so the booking form
  // goes), and whether any of them is (so the notice is owed). A class at a
  // blocked AND a working location gets the notice while staying bookable — the
  // catalog deliberately sent that answer here, and the enroll page's schedule
  // picker is where the blocked session itself becomes unselectable.
  //
  // `role="status"` (KTD8), kept to match the catalog's notice. Both pre-fetch branches show
  // the spinner on screen with the server summary below it (`SummaryBelowPlaceholder`), and
  // this notice arrives with the rest of the page once the fetch lands.
  const registrationLocked = program.registration_locked === true;
  const registrationLockNotice = registrationLocked || program.registration_locked_notice === true;
  const lockedLocationNames = (program.registration_locked_locations || [])
    .map((loc) => loc?.name)
    .filter((name): name is string => typeof name === 'string' && name.length > 0);
  const lockedLocationsPhrase =
    lockedLocationNames.length === 0
      ? null
      : lockedLocationNames.length === 1
        ? lockedLocationNames[0]
        : `${lockedLocationNames.slice(0, -1).join(', ')} and ${lockedLocationNames[lockedLocationNames.length - 1]}`;
  // The headline names the LOCATION, never the gym — a gym-wide sentence is
  // false at a gym whose other location still sells. With no name to use,
  // "Some" is right only while part of this offering still sells; when the whole
  // offering is blocked it would understate it.
  const registrationLockTitle = lockedLocationsPhrase
    ? `Registrations at ${lockedLocationsPhrase} aren't available right now`
    : registrationLocked
      ? "Registrations aren't available right now"
      : "Some registrations aren't available right now";

  // Gym-local "now" (resolved program timezone), computed once for the "Next
  // session" line AND the future-effective schedule-row annotation below.
  const gymNowParts = getGymNowParts(program.timezone);
  const gymTodayStr = gymNowParts.dateStr;
  // End of the availability lookahead window (today + lookahead) — the same end
  // the availability fetch uses. The line suppresses its spots fragment for a
  // pre-season session that lands beyond this date (item 4b).
  const availabilityWindowEnd = (() => {
    const base = parseDateLocal(gymTodayStr);
    return formatDateOnly(
      new Date(base.getFullYear(), base.getMonth(), base.getDate() + NEXT_SESSION_LOOKAHEAD_DAYS, 12)
    );
  })();

  // The "Next session" line's content. Day + time are computable from the
  // program payload alone (closed set = unavailable_dates until the
  // availability response lands, then the full union); the spots fragment
  // fills in when the response arrives. Null → render no line.
  // Suppressed when staff explicitly gated enrollment (waitlist_only/closed):
  // advertising live spots above a Join Waitlist / closed CTA is a copy conflict.
  const nextSessionLine = isOpenGym && !isWaitlistOnly && !isClosed
    ? buildNextSessionLine({
        sections: program.sections || [],
        unavailableDates: program.unavailable_dates || [],
        availability: nextSessionAvailability,
        availabilityRule: program.drop_in_availability_rule,
        extraDropInsAllowed: program.extra_drop_ins_allowed,
        gymNow: gymNowParts,
        // Session-based bounds (null for ongoing classes): pre-season shows the
        // season's first session; past the end date the line disappears.
        sessionStartDate: program.session_start_date || null,
        sessionEndDate: program.session_end_date || null,
        // Beyond the availability window, spots would be computed from data that
        // never covered the date — show day + time only (item 4b).
        availabilityWindowEndDate: availabilityWindowEnd,
      })
    : null;

  const formatCoachDisplayName = (name: string | null | undefined): string | null => {
    if (!name?.trim()) return null;
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0];
    return `${parts[0]} ${parts[parts.length - 1].charAt(0)}.`;
  };

  const getInstructorDisplayName = (instructor: {
    display_name?: string | null;
    preferred_name?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    gender?: string | null;
  } | null | undefined): string | null => {
    if (!instructor) return null;
    const genderLabel = instructor.gender && instructor.gender !== 'prefer_not_to_say'
      ? ` (${GenderUtils.getGenderLabel(instructor.gender)})`
      : '';
    if (instructor.first_name?.trim()) {
      const lastInitial = instructor.last_name?.trim()?.charAt(0);
      return `${instructor.first_name.trim()}${lastInitial ? ` ${lastInitial}.` : ''}${genderLabel}`;
    }
    const displayName = formatCoachDisplayName(instructor.preferred_name || instructor.display_name);
    return displayName ? `${displayName}${genderLabel}` : null;
  };

  const summarizeSectionDays = (section: any) => {
    const { days, time } = summarizeWeeklyDays(section.schedule, section, gymTodayStr);
    const names = days.map((day) => WEEKDAY_NAMES_SHORT[day]);
    return {
      dayLabel: names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0] ?? null,
      dayCount: days.length,
      time: time ? `${formatTime(time.start_time)} - ${formatTime(time.end_time)}` : null,
    };
  };

  // Requirement summary: Class detail schedule cards mirror the catalog modal so families see section availability, rates, and time-slot coaches in one scannable pattern.
  const renderScheduleSpotCount = (section: {
    spots_remaining?: number | null;
    waitlist_enabled?: boolean;
    registration_locked?: boolean;
  }) => {
    // U3 (KTD3): this schedule meets at a location whose payment cannot be
    // routed, so a spots pill would advertise something the parent cannot
    // book. Checked FIRST — on a class that runs at a blocked and a working
    // location this is the only per-session signal, since the booking form
    // above stays up for the sessions that still sell.
    if (section.registration_locked === true) {
      return (
        <span
          className="inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold bg-gray-100 text-gray-600 border-gray-200"
          data-testid="registration-locked-section-pill"
        >
          Not available right now
        </span>
      );
    }
    // Capacity is per session, so the lifetime spots pill is meaningless —
    // show plain-English per-session framing instead.
    if (isOpenGym) {
      return (
        <span className="inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold bg-green-50 text-green-700 border-green-200">
          Spots open up each session
        </span>
      );
    }
    if (typeof section.spots_remaining !== 'number') return null;

    const spots = section.spots_remaining;
    const waitlistOpen = section.waitlist_enabled || program.waitlist_enabled;
    const label = spots === 0
      ? waitlistOpen ? 'Waitlist Open' : 'Full'
      : `${spots} ${spots === 1 ? 'spot' : 'spots'} available`;
    const toneClass = spots === 0
      ? waitlistOpen
        ? 'bg-blue-50 text-blue-700 border-blue-200'
        : 'bg-red-50 text-red-700 border-red-200'
      : spots <= 3
      ? 'bg-amber-50 text-amber-700 border-amber-200'
      : 'bg-green-50 text-green-700 border-green-200';

    return (
      <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${toneClass}`}>
        {label}
      </span>
    );
  };

  const renderScheduleCoaches = (schedule: any) => {
    const instructors = Array.isArray(schedule?.instructors) ? schedule.instructors.filter(Boolean) : [];
    const displayableInstructors = instructors
      .map((instructor: any) => ({
        instructor,
        name: getInstructorDisplayName(instructor),
      }))
      .filter(({ name }: { name: string | null }) => Boolean(name));
    const fallbackNames = displayableInstructors.length > 0
      ? []
      : (schedule?.instructor_names || [schedule?.instructor_name])
        .map((name: string | null | undefined) => formatCoachDisplayName(name))
        .filter(Boolean);
    const coachCount = displayableInstructors.length || fallbackNames.length;

    if (coachCount === 0) return null;

    return (
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-500">
        <GoogleMaterialIcon name="person" size="sm" className="text-gray-400" />
        <span className="font-semibold text-gray-700">{coachCount === 1 ? 'Coach:' : 'Coaches:'}</span>
        {displayableInstructors.length > 0
          ? displayableInstructors.map(({ instructor, name }, instructorIndex: number) => (
            <span key={instructor.id || `${name}-${instructorIndex}`} className="inline-flex items-center gap-1.5">
              <CoachAvatar
                src={instructor.avatar_url || undefined}
                name={name}
                alt=""
                size={24}
                className="shrink-0"
              />
              <span>{name}{instructorIndex < displayableInstructors.length - 1 ? ',' : ''}</span>
            </span>
          ))
          : fallbackNames.map((name: string, index: number) => (
            <span key={`${name}-${index}`} className="inline-flex items-center gap-1.5">
              <CoachAvatar name={name} alt="" size={24} className="shrink-0" />
              <span>{name}{index < fallbackNames.length - 1 ? ',' : ''}</span>
            </span>
          ))}
      </div>
    );
  };

  return (
    <div
      className="min-h-screen bg-gray-50"
      style={{
        '--color-brand-gradient-start': primaryColor,
        '--color-brand-gradient-end': primaryColor,
        '--brand-primary': primaryColor,
      } as React.CSSProperties}
    >
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {organization?.logo_url ? (
                <img
                  src={organization.logo_url}
                  alt={organization.name}
                  className="h-12 w-auto"
                />
              ) : (
                <div>
                {/*
                  A `<p>`, NOT an `<h1>`. This page is about one PROGRAM, and the
                  program's name is its `<h1>` below — the same heading the
                  server summary owns before this component mounts, so the
                  document keeps one `<h1>` and keeps the SAME one across
                  hydration. Googlebot renders, so the heading it indexes is the
                  hydrated one; a gym name here would have every one of a gym's
                  program pages claiming the gym as its subject and competing
                  with the gym's own catalog page. It also fixes an older gap:
                  this branch only renders when the gym has no logo, so a gym
                  WITH a logo previously produced a page with no `<h1>` at all.
                */}
                  <p className="text-2xl font-bold text-gray-900">{organization?.name}</p>
                  <p className="text-sm text-gray-600">Program Details</p>
                </div>
              )}
            </div>
            <div className={`flex items-center gap-2${isMobileWebView ? ' hidden' : ''}`}>
              {currentUser ? (
                <>
                  {/* NAME THE ACCOUNT on the menu button itself
                      (#4897) — see SignedInAsBadge for why this is a security
                      signal and not decoration. */}
                  <PublicAccountMenu user={currentUser} />
                </>
              ) : (
                <>
                  {/* Sign In is the primary (branded) CTA. Most cheer parents
                      arriving here already have an account; giving Create Account
                      visual priority caused duplicate-account 409s. Both buttons
                      preserve pathname + query string so section/utm params survive
                      the signin round-trip. */}
                  <button
                    onClick={() => {
                      const target = (pathname || `/programs/${gymSlug}`) + (typeof window !== 'undefined' ? window.location.search : '');
                      router.push(`/signin?redirectTo=${encodeURIComponent(target)}`);
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors hover:brightness-95"
                    style={{ backgroundColor: primaryColor, color: readableTextOn(primaryColor) }}
                  >
                    <GoogleMaterialIcon name="login" size="sm" />
                    Sign In
                  </button>
                  <button
                    onClick={() => {
                      const target = (pathname || `/programs/${gymSlug}`) + (typeof window !== 'undefined' ? window.location.search : '');
                      const params = new URLSearchParams({ redirectTo: target });
                      if (organization?.id) {
                        params.set('organizationId', organization.id);
                        if (organization?.name) params.set('organizationName', organization.name);
                      }
                      router.push(`/register?${params.toString()}`);
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <GoogleMaterialIcon name="person_add" size="sm" />
                    Create Account
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back to Programs Link */}
        <Link 
          href={`/programs/${gymSlug}`}
          className="inline-flex items-center gap-2 mb-6 transition-colors hover:opacity-80"
          style={{ color: brandTextColor(primaryColor, 0, SURFACE_GRAY_50) }}
        >
          <GoogleMaterialIcon name="arrow_back" size="sm" />
          Back to Programs
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-6">
          {/* Main Content */}
          <div className="space-y-6">
          {/* Program Header */}
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            {/* Cover Image */}
            {program.cover_image_url && (
              <div className="relative h-64 bg-gray-100">
                <img
                  src={program.cover_image_url}
                  alt={program.name}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">{program.name}</h1>
                  <div className="flex items-center gap-3 flex-wrap">
                    {/* Open Gym U5 (R7): distinct "Open Gym" identity badge replaces
                        the generic type label so the offering reads as its own thing. */}
                    {isOpenGym ? (
                      <span
                        className="inline-flex items-center gap-1.5 px-3 py-1 text-sm font-semibold rounded"
                        style={{ backgroundColor: `${primaryColor}1a`, color: brandTextColor(primaryColor, 10) }}
                      >
                        <GoogleMaterialIcon name="warehouse" size="sm" />
                        Open Gym
                      </span>
                    ) : (
                      <span
                        className="inline-block px-3 py-1 text-sm font-medium rounded"
                        style={{ backgroundColor: `${primaryColor}1a`, color: brandTextColor(primaryColor, 10) }}
                      >
                        {program.program_type === 'team' && program.team_type
                          ? program.team_type === 'competitive_teams'
                            ? 'All-Star Team'
                            : 'Rec Team'
                          : getProgramTypeLabel(program.program_type)
                        }
                      </span>
                    )}
                    {program.program_type === 'team' && program.season_name && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium text-gray-700 bg-gray-50 rounded">
                        <GoogleMaterialIcon name="calendar_today" size="sm" />
                        {program.season_name}
                      </span>
                    )}
                    {program.skill_level && (
                      <span className="text-sm text-gray-600">
                        Skill Level: {program.skill_level}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Quick Info */}
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-4 text-gray-600">
                  {program.age_range_min && program.age_range_max && (
                    <div className="flex items-center gap-2">
                      <GoogleMaterialIcon name="cake" size="sm" />
                      <span className="text-sm">
                        Ages {program.age_range_min}-{program.age_range_max}
                      </span>
                    </div>
                  )}
                  {/* #4724: an ongoing class listed before its first class. Lives here,
                      not in the "Runs" block below, because that block is gated on
                      start_date/schedule_description and neither is set for a class. */}
                  {program.starts_on && (
                    <div className="flex items-center gap-2">
                      <GoogleMaterialIcon name="event" size="sm" />
                      <span className="text-sm font-medium">Starts {formatDate(program.starts_on)}</span>
                    </div>
                  )}
                  {/* schedule_description intentionally not shown here — the Schedule card below always renders it */}
                  {/* Location Display */}
                  {(program.location || (program.schedule_locations && program.schedule_locations.length > 0)) && (
                    <div className="flex items-center gap-2">
                      <GoogleMaterialIcon name="location_on" size="sm" />
                      <span className="text-sm">
                        {program.location 
                          ? program.location.name
                          : program.schedule_locations.length === 1
                            ? program.schedule_locations[0].name
                            : `${program.schedule_locations.length} locations`
                        }
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

            {/* Description */}
            {program.description && (
              <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <GoogleMaterialIcon name="description" size="sm" className="text-gray-500" />
                                  {/*
                  `h2`, NOT `h3`, AND EVERY NESTED HEADING MOVED WITH IT. The
                  page used to read `h1` gym name → `h2` program name → `h3`
                  cards → `h4` items. U5 made the program's name the page's
                  single `h1`, so the whole outline shifts up one: cards are
                  `h2` and the headings inside them are `h3`. Promoting only the
                  cards would have left `h2` → `h4` jumps under Coach, Available
                  Schedule and Pricing — a heading-order break where there was
                  none, which is the opposite of the fix. `singleH1.test.tsx` renders
                  this component with its fetches resolving and counts the
                  headings in the DOM; `headingOrder.test.tsx` sweeps the source
                  as a cheaper complement. A source sweep cannot tell which
                  branch rendered, which is why the DOM one holds the property.
                */}
                  <h2 className="text-lg font-semibold text-gray-900">Description</h2>
                </div>
                <p className="text-gray-700 whitespace-pre-wrap">{program.description}</p>
              </div>
            )}

            {/* Features */}
            {program.features && program.features.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <GoogleMaterialIcon name="star" size="sm" className="text-gray-500" />
                  <h2 className="text-lg font-semibold text-gray-900">What You'll Learn</h2>
                </div>
                <ul className="space-y-2">
                  {program.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2 text-gray-700">
                      <GoogleMaterialIcon name="check_circle" size="sm" className="text-green-500 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Class Schedule */}
            {program.program_type === 'class' &&
             (((program as any).sections && (program as any).sections.length > 0) ||
              ((program as any).class_schedule && (program as any).class_schedule.length > 0) ||
              program.start_date || program.end_date || program.schedule_description) && (
              <div>
                <div className="space-y-5">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-900">
                      Available Schedule
                    </h2>
                    {classSections.length > 0 && (
                      <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium uppercase tracking-wider text-gray-600">
                        {classSections.length} {classSections.length === 1 ? 'Schedule' : 'Schedules'}
                      </span>
                    )}
                  </div>

                  {/* Program Dates - shown right below title for context */}
                  {(program.start_date || program.end_date || program.schedule_description) && (
                    <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                      <div className="space-y-2 text-sm text-gray-700">
                        {program.schedule_description && (
                          <div className="flex items-center gap-2">
                            <GoogleMaterialIcon name="info" size="sm" className="text-gray-400" />
                            <span>{program.schedule_description}</span>
                          </div>
                        )}
                        {/* One "Runs" line instead of separate Start/End rows
                            — matches how families read program listings. Raw
                            date strings go into formatDate (never
                            new Date(dateOnly) — UTC-midnight footgun). */}
                        {(program.start_date || program.end_date) && (
                          <div className="flex items-center gap-2">
                            <GoogleMaterialIcon name="calendar_today" size="sm" className="text-gray-400" />
                            <span className="font-medium">
                              {program.start_date && program.end_date
                                ? program.start_date === program.end_date
                                  ? `On ${formatDate(program.start_date)}`
                                  : `Runs ${formatDate(program.start_date)} – ${formatDate(program.end_date)}`
                                : program.start_date
                                  ? `Starts ${formatDate(program.start_date)}`
                                  : `Through ${formatDate(program.end_date as string)}`}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Display sections if they exist, otherwise fall back to flat schedule */}
                  {classSections.length > 0 ? (
                    <div className="grid gap-8">
                      {classSections.map((section: any, sectionIndex: number) => {
                        const sectionLocation = program.schedule_locations?.find((loc: any) => loc.id === section.location_id);
                        const schedules = Array.isArray(section.schedule) ? [...section.schedule].sort((a: any, b: any) => a.day_of_week - b.day_of_week) : [];
                        const locationName = section.location_name || sectionLocation?.name;
                        const daySummary = summarizeSectionDays(section);

                        return (
                          <div
                            key={section.id || sectionIndex}
                            className="overflow-hidden rounded-2xl border border-gray-200 bg-white"
                            data-testid="schedule-group-card"
                          >
                            <div
                              className="border-b border-gray-200 p-5"
                              style={{ backgroundColor: `color-mix(in srgb, ${primaryColor} 8%, white)` }}
                            >
                              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                                <div className="flex min-w-0 flex-1 items-start gap-3">
                                  <div className="min-w-0 flex-1">
                                  <h3 className="text-lg font-bold text-gray-900">
                                    {section.section_name || `Schedule ${sectionIndex + 1}`}
                                    {daySummary.dayLabel && (
                                      <span className="font-semibold" style={{ color: brandTextColor(primaryColor, 8) }}>
                                        {' '}· {daySummary.dayLabel}
                                      </span>
                                    )}
                                  </h3>
                                  {daySummary.dayCount > 0 && (
                                    <p className="mt-0.5 text-sm text-gray-600">
                                      {daySummary.time ? `${daySummary.time} · ` : ''}
                                      {daySummary.dayCount} {daySummary.dayCount === 1 ? 'day' : 'days'} a week
                                    </p>
                                  )}

                                  <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-600">
                                    {renderScheduleSpotCount(section)}
                                    {section.capacity > 0 && (
                                      <div className="flex items-center gap-2">
                                        <GoogleMaterialIcon name="people" size="sm" className="text-gray-400" />
                                        <span>Capacity: {section.capacity} {isOpenGym ? 'per session' : 'students'}</span>
                                      </div>
                                    )}
                                    {locationName && (
                                      <div className="flex items-center gap-2">
                                        <GoogleMaterialIcon name="location_on" size="sm" className="text-gray-400" />
                                        <span>{locationName}</span>
                                      </div>
                                    )}
                                  </div>
                                  </div>
                                </div>

                                {/* The "Rates" heading renders only when at least one rate is
                                    actually shown — otherwise a class with drop-ins off and no
                                    eligible monthly rate leaves a bare label over empty
                                    space (#3358). */}
                                {(((program as any).drop_in_enabled !== false && (section.drop_in_rate ?? program.drop_in_rate) != null)
                                  || ((section.monthly_rate ?? program.monthly_rate) != null && classSellsMonthly(program as any) && program.billing_type !== 'one_time' && !(program as any).is_session_based)) ? (
                                <div className="flex-shrink-0 sm:text-right">
                                  <div className="mb-1 text-xs font-bold uppercase tracking-widest text-gray-400">Rates</div>
                                  <div className="flex items-baseline gap-3">
                                    {/* #3358: gate on the toggle, not just on a price being
                                        present. The schedule carries the class drop-in rate, so
                                        "Allow Drop-Ins" OFF was still advertising a live rate
                                        here. The four other drop-in sites in this file — the
                                        pricing-card selector, the pricing-summary row, the CTA
                                        branch, and the drop-in button block — already gate on
                                        drop_in_enabled; this one did not. (Named, not numbered:
                                        an earlier version of this comment cited line numbers and
                                        its own growth pushed all four out of date.)
                                        `!== false` keeps a missing flag meaning ENABLED, matching
                                        the column default (DEFAULT true). NOTE the four spell it
                                        as a plain truthy check, which reads a missing flag the
                                        OPPOSITE way. That is inert only because the detail route
                                        normalises with `?? true` inside mappedProgram before the
                                        client sees it — but that normalisation lives in the
                                        class-only conditional spread, so a program with no
                                        `classes` row still arrives here with the key absent. Do
                                        not "align" these spellings without re-checking that. */}
                                    {(program as any).drop_in_enabled !== false && (section.drop_in_rate ?? program.drop_in_rate) != null && (
                                      <div className="flex flex-col sm:items-end">
                                        <span className="text-lg font-bold" style={{ color: brandTextColor(primaryColor, 8) }}>
                                          ${section.drop_in_rate ?? program.drop_in_rate}
                                        </span>
                                        <span className="text-[10px] font-semibold uppercase" style={{ color: brandTextColor(primaryColor, 8) }}>
                                          Drop-in
                                        </span>
                                      </div>
                                    )}
                                    {/* #2116: never advertise a monthly membership rate on a session-based
                                        (fixed-term) class — it can hold a stale monthly_rate from before it was
                                        switched to session pricing, which the editor hides and the owner can't clear. */}
                                    {(section.monthly_rate ?? program.monthly_rate) != null && classSellsMonthly(program as any) && program.billing_type !== 'one_time' && !(program as any).is_session_based && (
                                      <div className="flex flex-col sm:items-end border-l border-gray-200 pl-3">
                                        <span className="text-lg font-bold text-gray-900">
                                          ${section.monthly_rate ?? program.monthly_rate}/mo
                                        </span>
                                        <span className="text-[10px] font-semibold uppercase text-gray-500">Membership</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                ) : null}
                              </div>
                            </div>

                            <div className="px-5 py-2">
                              {schedules.length > 0 ? (
                                <div className="divide-y divide-gray-100">
                                  {schedules.map((schedule: any, index: number) => {
                                    const scheduleLocationName = schedule.location_id && schedule.location_id !== section.location_id
                                      ? program.schedule_locations?.find((loc: any) => loc.id === schedule.location_id)?.name || schedule.location_name
                                      : null;

                                    return (
                                      <div
                                        key={schedule.id || index}
                                        className="flex items-center gap-4 py-3"
                                      >
                                        <div
                                          className="flex h-12 w-12 flex-shrink-0 flex-col items-center justify-center rounded-lg"
                                          style={{ backgroundColor: `color-mix(in srgb, ${primaryColor} 12%, white)`, color: brandTextColor(primaryColor, 12) }}
                                        >
                                          <span className="text-xs font-bold uppercase leading-none">
                                            {getDayName(schedule.day_of_week).substring(0, 3)}
                                          </span>
                                          <GoogleMaterialIcon name="schedule" size="sm" className="mt-0.5 opacity-75" />
                                        </div>

                                        <div className="min-w-0 flex-1">
                                          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                                            <span className="font-semibold text-gray-900">
                                              {getDayName(schedule.day_of_week)}
                                            </span>
                                            <span className="text-gray-400">•</span>
                                            <span className="font-semibold" style={{ color: brandTextColor(primaryColor) }}>
                                              {formatTime(schedule.start_time)} - {formatTime(schedule.end_time)}
                                            </span>
                                          </div>
                                          {/* Item 3: a not-yet-effective row (a schedule changeover starting
                                              later) is labelled so it never reads as currently meeting. Open
                                              gyms, plus (#4724) a class with a First class date — its weekly
                                              rows all start on that date. Other regular class pages forward
                                              future rows unannotated and stay unchanged (R5). Gym-local string
                                              compare; formatDate takes the raw date string (date-only footgun). */}
                                          {(isOpenGym || !!program.starts_on) && !isAddedDateSlot(schedule, program as any) && schedule.effective_date &&
                                            String(schedule.effective_date).slice(0, 10) > gymTodayStr && (
                                            <div className="mt-1">
                                              <span className="inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                                                Starting {formatDate(schedule.effective_date)}
                                              </span>
                                            </div>
                                          )}
                                          {/* #3354: a one-off added date meets exactly once —
                                              label the single date so it never reads as a weekly
                                              slot. Raw date string into formatDate (date-only
                                              footgun). */}
                                          {isAddedDateSlot(schedule, program as any) && (
                                            <div className="mt-1">
                                              <span className="inline-flex items-center gap-1 rounded bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                                                One-time class on {formatDate(toDateOnly(schedule.effective_date) as string)}
                                              </span>
                                            </div>
                                          )}
                                          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                                            {scheduleLocationName && (
                                              <div className="flex items-center gap-1 text-sm text-gray-500">
                                                <GoogleMaterialIcon name="location_on" size="sm" className="text-gray-400" />
                                                <span>{scheduleLocationName}</span>
                                              </div>
                                            )}
                                            {renderScheduleCoaches(schedule)}
                                          </div>
                                          <SessionDatesList
                                            meetingDates={(schedule as any).meeting_dates}
                                            skippedDates={(schedule as any).skipped_dates}
                                            truncated={(schedule as any).meeting_dates_truncated}
                                          />
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <p className="text-sm italic text-gray-500">
                                  No schedule defined for this schedule
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {(program as any).class_schedule
                        .sort((a: any, b: any) => a.day_of_week - b.day_of_week)
                        .map((schedule: any, index: number) => (
                          <div
                            key={index}
                            className="flex items-center gap-4 rounded-2xl border border-gray-100 bg-gray-50 p-4"
                          >
                            <div
                              className="flex h-12 w-12 flex-shrink-0 flex-col items-center justify-center rounded-lg text-white shadow-sm"
                              style={{ backgroundColor: primaryColor, color: readableTextOn(primaryColor) }}
                            >
                              <span className="text-xs font-bold uppercase leading-none">
                                {getDayName(schedule.day_of_week).substring(0, 3)}
                              </span>
                              <GoogleMaterialIcon name="schedule" size="sm" className="mt-0.5 opacity-75" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                                <span className="font-semibold text-gray-900">
                                  {getDayName(schedule.day_of_week)}
                                </span>
                                <span className="text-gray-400">•</span>
                                <span className="font-semibold" style={{ color: brandTextColor(primaryColor, 0, SURFACE_GRAY_50) }}>
                                  {formatTime(schedule.start_time)} - {formatTime(schedule.end_time)}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}

                  {/* Enrollment Type Info */}
                  {(program as any).enrollment_type && (
                    <div className="border-t border-gray-200 pt-4">
                      <div className="flex items-center gap-2">
                        <GoogleMaterialIcon
                          name="info"
                          size="sm"
                          style={{ color: brandTextColor(primaryColor) }}
                        />
                        <span className="text-sm text-gray-700">
                          <span className="font-medium">Enrollment: </span>
                          {(program as any).enrollment_type === 'ongoing' ? 'Ongoing (enroll anytime)' :
                           (program as any).enrollment_type === 'fixed_term' ? 'Session Program' :
                           (program as any).enrollment_type}
                        </span>
                      </div>

                      {(program as any).session_start_date && (program as any).session_end_date && (
                        <div className="mt-2 flex items-center gap-2">
                          <GoogleMaterialIcon name="calendar_today" size="sm" className="text-gray-500" />
                          <span className="text-sm text-gray-700">
                            Session: {formatDate((program as any).session_start_date)}
                            {(program as any).session_end_date !== (program as any).session_start_date &&
                              ` - ${formatDate((program as any).session_end_date)}`}
                          </span>
                        </div>
                      )}

                      {(program as any).session_length_weeks && (
                        <div className="mt-2 flex items-center gap-2">
                          <GoogleMaterialIcon name="event_note" size="sm" className="text-gray-500" />
                          <span className="text-sm text-gray-700">
                            {(program as any).session_length_weeks} weeks per session
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Dates this class does not meet.
                #2119: this card used to be headed "Holiday Closures" and read
                "The gym will be closed on the following dates" while listing
                `unavailable_dates` — the per-class blocked list. That asserted
                a gym-wide closure the data does not describe (the gym is often
                open; just this class is off) AND omitted real gym-wide
                closings entirely. The copy now says only what the data
                supports. Session-based classes suppress the card altogether:
                their dates are shown inline on the schedule above, crossed
                out, sourced from the full closure union. */}
            {program.program_type === 'class' &&
             !(program as any).is_session_based &&
             (program as any).unavailable_dates &&
             (program as any).unavailable_dates.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <GoogleMaterialIcon name="event_busy" size="sm" className="text-gray-500" />
                  <h2 className="text-lg font-semibold text-gray-900">No Class On These Dates</h2>
                </div>

                <p className="text-sm text-gray-600 mb-3">
                  This class will not meet on the following dates:
                </p>

                <div className="space-y-2">
                  {(program as any).unavailable_dates.map((date: string, index: number) => (
                    <div key={index} className="flex items-center gap-2 text-gray-700 p-2 bg-gray-50 rounded">
                      <GoogleMaterialIcon name="cancel" size="sm" className="text-red-500" />
                      <span className="text-sm">
                        {parseDateLocal(date).toLocaleDateString('en-US', {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Schedule & Dates for non-class programs (e.g., teams) that have date info but no sections */}
            {program.program_type !== 'class' &&
             (program.start_date || program.end_date || program.schedule_description) && (
              <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <GoogleMaterialIcon name="calendar_today" size="sm" className="text-gray-500" />
                  <h2 className="text-lg font-semibold text-gray-900">Schedule</h2>
                </div>
                <div className="space-y-2 text-sm text-gray-700">
                  {program.schedule_description && (
                    <div className="flex items-center gap-2">
                      <GoogleMaterialIcon name="info" size="sm" className="text-gray-400" />
                      <span>{program.schedule_description}</span>
                    </div>
                  )}
                  {program.start_date && (
                    <div className="flex items-center gap-2">
                      <GoogleMaterialIcon name="calendar_today" size="sm" className="text-gray-400" />
                      <span>
                        <span className="font-medium">Start Date:</span>{' '}
                        {formatDate(program.start_date)}
                      </span>
                    </div>
                  )}
                  {program.end_date && (
                    <div className="flex items-center gap-2">
                      <GoogleMaterialIcon name="event" size="sm" className="text-gray-400" />
                      <span>
                        <span className="font-medium">End Date:</span>{' '}
                        {formatDate(program.end_date)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Coach */}
            {program.instructor_name && (
              <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <GoogleMaterialIcon name="person" size="sm" className="text-gray-500" />
                  <h2 className="text-lg font-semibold text-gray-900">Coach</h2>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">{program.instructor_name}</h3>
                  {program.instructor_credentials && (
                    <p className="text-sm text-gray-600 mb-2">{program.instructor_credentials}</p>
                  )}
                  {program.instructor_bio && (
                    <p className="text-gray-700">{program.instructor_bio}</p>
                  )}
                </div>
              </div>
            )}

            {/* Custom Message */}
            {program.public_settings?.custom_message && (
              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
                <div className="flex items-start gap-3">
                  <GoogleMaterialIcon name="info" size="sm" className="text-blue-600 mt-0.5" />
                  <p className="text-blue-900">{program.public_settings.custom_message}</p>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Pricing Card */}
            <div className="bg-white rounded-2xl shadow-theme-lg border border-gray-200 p-6 sticky top-4">
              <div className="flex items-center gap-2 mb-4">
                <GoogleMaterialIcon name="payments" size="sm" className="text-gray-500" />
                <h2 className="text-lg font-semibold text-gray-900">Pricing</h2>
              </div>

              <div className="space-y-4 mb-6">
                {program.program_type === 'class' ? (
                  <>
                    {/* Class Pricing — selectable cards when drop-in is available.
                        Open Gym U5 (R7): skip the dual enrollment/drop-in selector
                        for open gyms — there is no monthly enrollment, only
                        pay-per-visit. They fall through to the simple drop-in price
                        display below (which already carries the U6 member-rate line,
                        untouched). */}
                    {(!isOpenGym && (program as any).drop_in_enabled && program.drop_in_rate != null) ? (
                      <div className="space-y-3">
                        {/* Enrollment Card */}
                        <button
                          type="button"
                          onClick={() => setSelectedPricingOption('enrollment')}
                          className={`w-full text-left rounded-2xl p-4 transition-all ${
                            selectedPricingOption === 'enrollment'
                              ? ''
                              : 'border border-gray-200 hover:border-gray-300'
                          }`}
                          style={selectedPricingOption === 'enrollment' ? {
                            border: `2px solid ${primaryColor}`,
                            backgroundColor: `${primaryColor}08`,
                          } : undefined}
                        >
                          <div className="flex items-center gap-2 mb-3">
                            <div
                              className="w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                              style={selectedPricingOption === 'enrollment'
                                ? { borderColor: primaryColor }
                                : { borderColor: '#d1d5db' }
                              }
                            >
                              {selectedPricingOption === 'enrollment' && (
                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: primaryColor }} />
                              )}
                            </div>
                            <span className="text-sm font-semibold text-gray-900">Program Enrollment</span>
                          </div>

                          {(program as any).is_session_based ? (
                            <div className="space-y-2 pl-6">
                              {(program as any).session_total_price != null && (
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="text-sm text-gray-600">Program Price</p>
                                    <p className="text-xs text-gray-500">
                                      {(program as any).session_length_weeks
                                        ? `${(program as any).session_length_weeks}-week program`
                                        : 'Session Program'}
                                    </p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-xl font-bold text-gray-900">
                                      ${(program as any).session_total_price}
                                    </p>
                                    <p className="text-xs text-gray-500">total</p>
                                  </div>
                                </div>
                              )}
                              {isSessionPaymentPlan(program.session_payment_plan) && program.session_payment_plan !== 'full_upfront' && (
                                <div className="flex items-center gap-2 py-2 px-2.5 bg-blue-50 rounded-md border border-blue-100">
                                  <GoogleMaterialIcon name="credit_card" size="xs" className="text-blue-600" />
                                  <span className="text-xs font-medium text-blue-700">
                                    {`${SESSION_PAYMENT_PLAN_OPTIONS[program.session_payment_plan as SessionPaymentPlan]} available`}
                                  </span>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="space-y-2 pl-6">
                              {monthlyPriceShown && (
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="text-sm text-gray-600">Monthly</p>
                                    <p className="text-xs text-gray-500">Ongoing enrollment</p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-xl font-bold text-gray-900">
                                      ${program.monthly_rate}
                                    </p>
                                    <p className="text-xs text-gray-500">/month</p>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Family Discount & Packages inside enrollment card */}
                          {((program as any).package_pricing?.length > 0 || (program as any).family_discount_rate > 0) && (
                            <div className="space-y-1.5 pl-6 mt-2 pt-2 border-t border-gray-100">
                              {(program as any).package_pricing?.map((pkg: any, index: number) => (
                                <div key={index} className="flex items-center justify-between text-xs">
                                  <span className="text-gray-500">{pkg.sessions} Classes</span>
                                  <span className="text-gray-700 font-medium">${pkg.price}</span>
                                </div>
                              ))}
                              {(program as any).family_discount_rate > 0 && (
                                <div className="flex items-center justify-between text-xs">
                                  <span className="text-gray-500">Family Discount</span>
                                  <span className="text-green-600 font-medium">{(program as any).family_discount_rate}% off</span>
                                </div>
                              )}
                            </div>
                          )}
                        </button>

                        {/* Drop-in Card */}
                        <button
                          type="button"
                          onClick={() => setSelectedPricingOption('drop_in')}
                          className={`w-full text-left rounded-2xl p-4 transition-all ${
                            selectedPricingOption === 'drop_in'
                              ? ''
                              : 'border border-gray-200 hover:border-gray-300'
                          }`}
                          style={selectedPricingOption === 'drop_in' ? {
                            border: `2px solid ${primaryColor}`,
                            backgroundColor: `${primaryColor}08`,
                          } : undefined}
                        >
                          <div className="flex items-center gap-2 mb-3">
                            <div
                              className="w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                              style={selectedPricingOption === 'drop_in'
                                ? { borderColor: primaryColor }
                                : { borderColor: '#d1d5db' }
                              }
                            >
                              {selectedPricingOption === 'drop_in' && (
                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: primaryColor }} />
                              )}
                            </div>
                            <span className="text-sm font-semibold text-gray-900">Drop-in</span>
                          </div>

                          <div className="pl-6">
                            <div className="flex items-center justify-between">
                              <p className="text-sm text-gray-600">Single class</p>
                              <div className="text-right">
                                <p className="text-xl font-bold text-gray-900">
                                  ${program.drop_in_rate}
                                </p>
                                <p className="text-xs text-gray-500">/class</p>
                              </div>
                            </div>
                            {memberDropInPriceShown != null && (
                              <p className="text-[10px] text-blue-600 font-bold bg-blue-50 px-1.5 py-0.5 rounded mt-1 uppercase tracking-tighter italic inline-block">
                                ${memberDropInPriceShown} for members
                              </p>
                            )}
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={(e) => { e.stopPropagation(); setShowDropInInfo(true); }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setShowDropInInfo(true);
                                }
                              }}
                              className="block text-xs font-medium text-gray-500 hover:text-gray-700 underline underline-offset-4 decoration-gray-300 transition-colors mt-2"
                            >
                              What&apos;s drop-in?
                            </span>
                          </div>
                        </button>
                      </div>
                    ) : (
                      <>
                        {/* No drop-in — show flat pricing layout */}
                        {(program as any).is_session_based ? (
                          <>
                            {(program as any).session_total_price != null && (
                              <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                                <div>
                                  <p className="text-sm text-gray-600">Program Price</p>
                                  <p className="text-xs text-gray-500">
                                    {(program as any).session_length_weeks
                                      ? `${(program as any).session_length_weeks}-week program`
                                      : 'Session Program'}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="text-2xl font-bold text-gray-900">
                                    ${(program as any).session_total_price}
                                  </p>
                                  <p className="text-xs text-gray-500">total</p>
                                </div>
                              </div>
                            )}
                            {isSessionPaymentPlan(program.session_payment_plan) && program.session_payment_plan !== 'full_upfront' && (
                              <div className="flex items-center gap-2 py-3 px-3 bg-blue-50 rounded-lg border border-blue-100">
                                <GoogleMaterialIcon name="credit_card" size="xs" className="text-blue-600" />
                                <span className="text-xs font-medium text-blue-700">
                                  {`${SESSION_PAYMENT_PLAN_OPTIONS[program.session_payment_plan as SessionPaymentPlan]} available`}
                                </span>
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            {monthlyPriceShown && (
                              <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                                <div>
                                  <p className="text-sm text-gray-600">Monthly</p>
                                  <p className="text-xs text-gray-500">Ongoing enrollment</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-2xl font-bold text-gray-900">
                                    ${program.monthly_rate}
                                  </p>
                                  <p className="text-xs text-gray-500">/month</p>
                                </div>
                              </div>
                            )}
                          </>
                        )}

                        {/* Packages & Discounts for non-drop-in classes */}
                        {((program as any).package_pricing?.length > 0 || (program as any).family_discount_rate > 0) && (
                          <div className="space-y-2">
                            {((program as any).package_pricing?.length > 0) && (
                              <div className="pt-2 space-y-2">
                                <p className="text-xs font-medium text-gray-900 uppercase">Package Options</p>
                                {(program as any).package_pricing?.map((pkg: any, index: number) => (
                                  <div key={index} className="flex items-center justify-between text-sm">
                                    <span className="text-gray-600">{pkg.sessions} Classes</span>
                                    <span className="text-gray-900 font-medium">${pkg.price}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {(program as any).family_discount_rate > 0 && (
                              <div className={`flex items-center justify-between text-sm ${((program as any).package_pricing?.length > 0) ? 'pt-2 border-t border-gray-200' : ''}`}>
                                <span className="text-gray-600">Family Discount</span>
                                <span className="text-green-600 font-medium">{(program as any).family_discount_rate}% off</span>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    {(program as any).makeup_tokens_enabled && (
                      <div className="flex items-center gap-2 py-3 px-3 bg-green-50 rounded-lg border border-green-100 mb-4 animate-in fade-in zoom-in duration-500">
                        <GoogleMaterialIcon name="confirmation_number" size="xs" className="text-green-600" />
                        <span className="text-[11px] font-bold text-green-700 leading-tight uppercase tracking-wider">
                          Make-up Classes Available
                        </span>
                      </div>
                    )}
                  </>
                ) : program.program_type === 'team' ? (
                  <>
                    {/* Team Program Pricing */}
                    {(program as any).registration_fee && (
                      <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                        <div>
                          <p className="text-sm text-gray-600">Registration Fee</p>
                          <p className="text-xs text-gray-500">One-time payment</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-gray-900">
                            ${(program as any).registration_fee}
                          </p>
                        </div>
                      </div>
                    )}

                    {(program as any).monthly_tuition && (
                      <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                        <div>
                          <p className="text-sm text-gray-600">Monthly Tuition</p>
                          <p className="text-xs text-gray-500">Per month</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-gray-900">
                            ${(program as any).monthly_tuition}
                          </p>
                          <p className="text-xs text-gray-500">/month</p>
                        </div>
                      </div>
                    )}

                    {(program as any).uniform_cost && (
                      <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                        <div>
                          <p className="text-sm text-gray-600">Uniform</p>
                          <p className="text-xs text-gray-500">One-time purchase</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-semibold text-gray-900">
                            ${(program as any).uniform_cost}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Additional Team Costs */}
                    {((program as any).competition_budget || (program as any).travel_budget) && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-gray-900 uppercase pt-2">Additional Costs</p>
                        {(program as any).competition_budget && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">Competition Budget</span>
                            <span className="text-gray-900 font-medium">${(program as any).competition_budget}</span>
                          </div>
                        )}
                        {(program as any).travel_budget && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">Travel Budget</span>
                            <span className="text-gray-900 font-medium">${(program as any).travel_budget}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {/* Generic Class Pricing */}
                    {monthlyPriceShown && (
                      <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                        <div>
                          <p className="text-sm text-gray-600">Monthly</p>
                          <p className="text-xs text-gray-500">Ongoing enrollment</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-gray-900">
                            ${program.monthly_rate}
                          </p>
                          <p className="text-xs text-gray-500">/month</p>
                        </div>
                      </div>
                    )}

                    {program.drop_in_enabled && program.drop_in_rate != null && (
                      <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                        <div>
                          <p className="text-sm text-gray-600">Drop-in</p>
                          <p className="text-xs text-gray-500">Single class</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-semibold text-gray-900">
                            ${program.drop_in_rate}
                          </p>
                          <p className="text-xs text-gray-500">/class</p>
                          {memberDropInPriceShown != null && (
                            <p className="text-[10px] text-green-700 font-semibold mt-0.5">
                              ${memberDropInPriceShown} for members
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                  </>
                )}
              </div>

              {classHasPaidPrice && <PublicPriceExtrasNote programId={program.id} />}

              {/* Additional fees notice */}
              <div className="flex items-start gap-2 px-3 py-2.5 bg-gray-50 rounded-2xl border border-gray-100 mb-6">
                <GoogleMaterialIcon name="info" size="xs" className="text-gray-400 mt-0.5 flex-shrink-0" />
                <p className="text-[11px] text-gray-500 leading-relaxed">
                  {(program as any).annual_membership_required === false
                    ? 'No annual gym membership fee is required for this class.'
                    : classHasPaidPrice
                      ? 'An annual gym membership fee may be added at checkout, depending on your membership status.'
                      : 'Annual gym membership fee and a small platform fee may be added at checkout, depending on your membership status.'}
                </p>
              </div>

              {/* Availability Status */}
              {/* Hide overall availability for programs with sections - section-level badges show availability */}
              {/* U3: "Open for Enrollment" / "N spots available" are statements
                  about whether this offering can be joined, and they are false
                  above a notice saying it cannot. Suppressed rather than
                  reworded — the notice is the answer. */}
              <div className="mb-6">
                {!registrationLocked &&
                 !(program.program_type === 'class' &&
                   (program as any).sections && (program as any).sections.length > 0) && (
                  <>
                    <div className="flex items-center gap-2 mb-2">
                      <GoogleMaterialIcon name="inventory" size="sm" className="text-gray-500" />
                      <h3 className="text-sm font-medium text-gray-900">Availability</h3>
                    </div>

                    {isClosed ? (
                      <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
                        <GoogleMaterialIcon name="cancel" size="sm" className="text-red-600" />
                        <div>
                          <p className="text-sm font-semibold text-red-900">{programEnded ? 'Class Ended' : 'Enrollment Closed'}</p>
                          <p className="text-xs text-red-700">Not accepting new students</p>
                        </div>
                      </div>
                    ) : isWaitlistOnly ? (
                      <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                        <GoogleMaterialIcon name="hourglass_empty" size="sm" className="text-yellow-600" />
                        <div>
                          <p className="text-sm font-semibold text-yellow-900">Waitlist Only</p>
                          <p className="text-xs text-yellow-700">
                            {program.registration_type === 'evaluation_required'
                              ? 'Evaluation event is full - join waitlist'
                              : 'No spots currently available'}
                          </p>
                        </div>
                      </div>
                    ) : program.spots_remaining <= 5 && program.spots_remaining > 0 ? (
                      <div className="flex items-center gap-2 p-3 bg-orange-50 border border-orange-200 rounded-md">
                        <GoogleMaterialIcon name="warning" size="sm" className="text-orange-600" />
                        <div>
                          <p className="text-sm font-semibold text-orange-900">
                            {program.spots_remaining} {program.registration_type === 'evaluation_required' ? 'evaluation spots' : 'spots'} left
                          </p>
                          <p className="text-xs text-orange-700">Limited availability</p>
                        </div>
                      </div>
                    ) : program.spots_remaining > 5 && program.spots_remaining < 999 ? (
                      <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-md">
                        <GoogleMaterialIcon name="check_circle" size="sm" className="text-green-600" />
                        <div>
                          <p className="text-sm font-semibold text-green-900">
                            {program.spots_remaining} {program.registration_type === 'evaluation_required' ? 'evaluation spots' : 'spots'} available
                          </p>
                          <p className="text-xs text-green-700">Open for enrollment</p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-md">
                        <GoogleMaterialIcon name="check_circle" size="sm" className="text-green-600" />
                        <div>
                          <p className="text-sm font-semibold text-green-900">
                            {program.registration_type === 'evaluation_required' ? 'Evaluations Open' : 'Open for Enrollment'}
                          </p>
                          <p className="text-xs text-green-700">Accepting registrations</p>
                        </div>
                      </div>
                    )}
                  </>
                )}
                
                {/* Waitlist Information. Suppressed for open gyms — "if class
                    fills up" contradicts per-session capacity (each session's
                    spots reset; the class never "fills up"). */}
                {/* U3: "Waitlist available if class fills up" / "Join the
                    waitlist" offer a way in, and joining a waitlist is blocked
                    here too — the CTA below is replaced by the notice. Dropped
                    rather than reworded, for the same reason the availability
                    block above is. */}
                {program.program_type === 'class' &&
                 !isOpenGym &&
                 !registrationLocked &&
                 !isClosed &&
                 (program as any).waitlist_enabled && (
                  <div 
                    className="mt-3 p-2 rounded-md border"
                    style={{ backgroundColor: `${primaryColor}0d`, borderColor: `${primaryColor}33` }}
                  >
                    <div className="flex items-center gap-2">
                      <GoogleMaterialIcon 
                        name="playlist_add" 
                        size="sm" 
                        style={{ color: brandTextColor(primaryColor, 5) }}
                      />
                      <p 
                        className="text-xs"
                        style={{ color: brandTextColor(primaryColor, 5) }}
                      >
                        {isWaitlistOnly 
                          ? 'Join the waitlist - the gym will email you if a spot is offered' 
                          : 'Waitlist available if class fills up'}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* "Next session" line — day + time render as soon as the
                  program payload allows; the spots fragment fills a
                  reserved-width span when the availability response lands so
                  its arrival never shifts layout. Renders above the CTA area
                  — including above the members-only notice, time-only, since
                  availability 403s for locked viewers. */}
              {isOpenGym && nextSessionLine && (
                <div
                  className="mb-4 rounded-2xl border px-3 py-2.5"
                  style={{ backgroundColor: `${primaryColor}0d`, borderColor: `${primaryColor}33` }}
                >
                  <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm text-gray-800">
                    <GoogleMaterialIcon name="schedule" size="sm" style={{ color: brandTextColor(primaryColor, 5) }} />
                    <span className="font-semibold" style={{ color: brandTextColor(primaryColor, 5) }}>
                      Next session:
                    </span>
                    <span>
                      {nextSessionLine.dayLabel} · {formatTime(nextSessionLine.startTime)} –{' '}
                      {formatTime(nextSessionLine.endTime)}
                    </span>
                    <span className="inline-block min-w-[88px] font-medium">
                      {nextSessionLine.spotsText ? `· ${nextSessionLine.spotsText}` : ' '}
                    </span>
                  </p>
                </div>
              )}

              {/* Staff of this gym: walk the parent sign-up with a sample athlete (#5634). Outside the
                  call-to-action chain below on purpose, so locked, members-only and ended pages show it too. */}
              <PreviewAsParentButton
                programId={programId}
                surface="class"
                enabled={!!currentUser && params.programType === 'class'}
                enrollHref={previewEnrollHref}
              />

              {/* Call to Action Buttons */}
              <div className="space-y-3">
                {/* Partly blocked: the offering still sells at a working
                    location, so the notice sits ABOVE the booking form rather
                    than replacing it. Fully blocked, the same notice IS the
                    branch below. */}
                {registrationLockNotice && !registrationLocked && (
                  <Alert
                    variant="warning"
                    role="status"
                    title={registrationLockTitle}
                    data-testid="registration-locked-notice"
                  />
                )}
                {registrationLocked ? (
                  /* U3 (R2 / D1): every location this offering runs at is
                     blocked, so the booking form is replaced by the notice.
                     Schedule and price stay visible above — "disable, don't
                     hide". The purchase routes are the real gate; this is the
                     answer the parent gets before filling anything in. */
                  <Alert
                    variant="warning"
                    role="status"
                    title={registrationLockTitle}
                    data-testid="registration-locked-notice"
                  />
                ) : membersOnlyLocked ? (
                  /* Open Gym U4/U5 (R6): members-only open gym the viewer can't book.
                     Plain-English notice REPLACES the booking form (server is the
                     real gate; this is the friendly heads-up). We keep the schedule
                     and price visible above to motivate joining. */
                  <div className="rounded-2xl p-4 space-y-2 border border-amber-200 bg-amber-50">
                    <div className="flex items-center gap-2">
                      <GoogleMaterialIcon name="lock" size="sm" className="text-amber-600" />
                      <h3 className="text-sm font-semibold text-amber-900">Members only</h3>
                    </div>
                    <p className="text-sm text-amber-800 leading-relaxed">
                      This Open Gym is for active members only. Not a member yet?
                      Contact the gym to join and unlock booking.
                    </p>
                    {!currentUser && (
                      <button
                        onClick={() => router.push(`/signin?redirectTo=${encodeURIComponent(pathname || `/programs/${gymSlug}`)}`)}
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-900 hover:text-amber-700 transition-colors"
                      >
                        <GoogleMaterialIcon name="login" size="sm" />
                        Already a member? Sign in
                      </button>
                    )}
                  </div>
                ) : selfServiceUnavailable ? (
                  <StaffRegistrationMessage size="md" gymName={currentOrganization?.name} />
                ) : isInvitedAthlete ? (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4 space-y-2">
                    <div className="flex items-center gap-2">
                      <GoogleMaterialIcon name="warning" size="sm" className="text-yellow-600" />
                      <h3 className="text-sm font-semibold text-yellow-900">Registration Restricted</h3>
                    </div>
                    <p className="text-xs text-yellow-800 leading-relaxed">
                      Please ask your parent or guardian to complete this registration.
                    </p>
                  </div>
                ) : organization?.roster_cap_blocked === true && !currentUser ? (
                  /* Roster limit: a signed-out visitor can only come in as a new
                     family, and a new athlete cannot be added. Same sentence as
                     the tryout sign-up page. */
                  <div className="w-full px-4 py-4 bg-gray-50 border border-gray-200 rounded-2xl text-sm text-gray-600 text-center">
                    <GoogleMaterialIcon name="info" size="sm" className="mb-1 block mx-auto text-gray-400" />
                    This gym isn&apos;t accepting new athlete registrations online right now — please contact the gym directly.
                  </div>
                ) : familyGate.blockAllInActiveFamily ? (
                  /* Family access gate (same sentence as the private-lesson page
                     and the server). */
                  <div className="w-full px-4 py-4 bg-gray-50 border border-gray-200 rounded-2xl text-sm text-gray-600 text-center">
                    <GoogleMaterialIcon name="lock" size="sm" className="mb-1 block mx-auto text-gray-400" />
                    {CANNOT_REGISTER_ATHLETE_MESSAGE}
                  </div>
                ) : program.registration_type === 'evaluation_required' ? (
                  <div 
                    className="rounded-2xl p-4 space-y-3 border"
                    style={{ backgroundColor: `${primaryColor}0d`, borderColor: `${primaryColor}33` }}
                  >
                    <div className="flex items-center gap-2">
                      <GoogleMaterialIcon 
                        name="info" 
                        size="sm" 
                        style={{ color: brandTextColor(primaryColor, 5) }}
                      />
                      <h3 
                        className="text-sm font-semibold"
                        style={{ color: brandTextColor(primaryColor, 5) }}
                      >
                        Evaluations Required
                      </h3>
                    </div>
                    <p 
                      className="text-sm leading-relaxed"
                      style={{ color: brandTextColor(primaryColor, 5) }}
                    >
                      This program requires an evaluation before enrollment. Please check the evaluation schedule to book your session.
                    </p>
                    <Button 
                      variant="primary"
                      style={{ color: readableTextOn(primaryColor) }}
                      className="w-full"
                      onClick={() => router.push(`/programs/${gymSlug}?type=team`)}
                      startIcon={<GoogleMaterialIcon name="calendar_today" size="sm" />}
                    >
                      View Evaluation Schedule
                    </Button>
                  </div>
                ) : (
                  <>
                    {/* Direct Enrollment Buttons */}
                    {isClosed ? (
                      <Button variant="outline" disabled className="w-full" startIcon={<GoogleMaterialIcon name="cancel" size="sm" />}>
                        {programEnded ? 'Class Ended' : 'Enrollment Closed'}
                      </Button>
                    ) : isWaitlistOnly ? (
                      <Button variant="primary" style={{ color: readableTextOn(primaryColor) }} onClick={handleJoinWaitlist} className="w-full" startIcon={<GoogleMaterialIcon name="hourglass_empty" size="sm" />}>
                        Join Waitlist
                      </Button>
                    ) : isOpenGym ? (
                      /* Open Gym U5 (R7): a single "Book a session" CTA that routes
                         straight to the canonical drop-in path (handleBookDropIn
                         sets type=drop_in). No monthly/enrollment option — open gym
                         is pay-per-visit only (server forces drop-in only in U2). */
                      <Button
                        variant="primary"
                        style={{ color: readableTextOn(primaryColor) }}
                        onClick={handleBookDropIn}
                        className="w-full py-6 text-lg font-bold"
                        startIcon={<GoogleMaterialIcon name="event_available" size="sm" />}
                      >
                        Book a session
                      </Button>
                    ) : program.program_type === 'class' && (program as any).drop_in_enabled && program.drop_in_rate != null ? (
                      <>
                        {/* Single Continue button for classes with selectable pricing cards */}
                        {selectedPricingOption === 'drop_in' && program.drop_in_availability_rule === 'only_if_not_full' && !currentUser ? (
                          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-center space-y-2">
                            <p className="text-sm font-medium text-amber-800">
                              Drop-ins for this class are only available to current members.
                            </p>
                            <button
                              onClick={() => router.push(`/signin?redirectTo=${encodeURIComponent(pathname || `/programs/${gymSlug}`)}`)}
                              className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-800 transition-colors"
                            >
                              <GoogleMaterialIcon name="login" size="sm" />
                              Sign in to book a drop-in
                            </button>
                          </div>
                        ) : (
                          <Button
                            variant="primary"
                            style={{ color: readableTextOn(primaryColor) }}
                            onClick={selectedPricingOption === 'drop_in' ? handleBookDropIn : handleEnrollNow}
                            className="w-full py-6 text-lg font-bold"
                            startIcon={<GoogleMaterialIcon name="arrow_forward" size="sm" />}
                          >
                            Continue
                          </Button>
                        )}
                      </>
                    ) : (
                      <>
                        {/* Standard buttons for non-class programs or classes without drop-in */}
                        <Button
                          variant="primary"
                          style={{ color: readableTextOn(primaryColor) }}
                          onClick={handleEnrollNow}
                          className="w-full py-6 text-lg font-bold"
                          startIcon={<GoogleMaterialIcon name="app_registration" size="sm" />}
                        >
                          {'Enroll Now'}
                        </Button>

                        {/* Drop-in Button for non-class programs that happen to have drop-in */}
                        {program.drop_in_enabled && (
                          <div className="space-y-2">
                            {program.drop_in_availability_rule === 'only_if_not_full' && !currentUser ? (
                              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-center space-y-2">
                                <p className="text-sm font-medium text-amber-800">
                                  Drop-ins for this class are only available to current members.
                                </p>
                                <button
                                  onClick={() => router.push(`/signin?redirectTo=${encodeURIComponent(pathname || `/programs/${gymSlug}`)}`)}
                                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-800 transition-colors"
                                >
                                  <GoogleMaterialIcon name="login" size="sm" />
                                  Sign in to book a drop-in
                                </button>
                              </div>
                            ) : (
                              <Button
                                variant="secondary"
                                onClick={handleBookDropIn}
                                className="w-full py-4 font-semibold"
                                startIcon={<GoogleMaterialIcon name="door_open" size="sm" />}
                              >
                                Book Drop-in
                              </Button>
                            )}
                            <button
                              onClick={() => setShowDropInInfo(true)}
                              className="w-full text-center text-xs font-medium text-gray-500 hover:text-gray-700 underline underline-offset-4 decoration-gray-300 transition-colors"
                            >
                              What&apos;s drop-in?
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Drop-in Info Modal */}
      <Modal
        isOpen={showDropInInfo}
        onClose={() => setShowDropInInfo(false)}
        className="max-w-md"
      >
        <Modal.Header>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20">
              <GoogleMaterialIcon name="door_open" className="text-blue-600 dark:text-blue-400" size="sm" />
            </div>
            <div>
              <Typography variant="h5" className="text-gray-900 dark:text-white">
                What is a Drop-in?
              </Typography>
              <Typography variant="body-sm" color="muted">
                Flexible single-class booking
              </Typography>
            </div>
          </div>
        </Modal.Header>

        <Modal.Content>
          <div className="space-y-4">
            <Typography variant="body" className="text-gray-600 dark:text-gray-300">
              A drop-in allows you to book and attend a single class session without committing to a full monthly membership or a multi-week program.
            </Typography>
            
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <GoogleMaterialIcon name="check_circle" size="xs" className="text-green-500 mt-1" />
                <Typography variant="body-sm" className="text-gray-700 dark:text-gray-200">
                  <strong>Flexible scheduling:</strong> Perfect for trying out a new class or visiting when your schedule allows.
                </Typography>
              </div>
              <div className="flex items-start gap-2">
                <GoogleMaterialIcon name="check_circle" size="xs" className="text-green-500 mt-1" />
                <Typography variant="body-sm" className="text-gray-700 dark:text-gray-200">
                  <strong>No commitment:</strong> Pay only for the session you attend.
                </Typography>
              </div>
              <div className="flex items-start gap-2">
                <GoogleMaterialIcon name="check_circle" size="xs" className="text-green-500 mt-1" />
                <Typography variant="body-sm" className="text-gray-700 dark:text-gray-200">
                  <strong>Simple booking:</strong> Just select your date, pay the drop-in rate, and join the class!
                </Typography>
              </div>
            </div>

            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-2xl border border-amber-100 dark:border-amber-900/30">
              <p className="text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
                <strong>Note:</strong> Drop-in availability is subject to class capacity. We recommend booking in advance to secure your spot.
              </p>
            </div>
          </div>
        </Modal.Content>

        <Modal.Footer variant="actions">
          <Button variant="primary" onClick={() => setShowDropInInfo(false)}>
            Got it
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
